import { useCallback, useEffect, useRef, useState } from "react";
import { DineInRound, DineInRoundItem } from "../../../../interfaces/DineInObject";
import { ProductDetailsModal } from "../../../../interfaces/ProductObject";
import {
  billableDineInItems,
  buildProductIndex,
  resolveDineInLine,
  selectPlacedRounds,
} from "../../../../services/helpers/DineInSummaryHelper";
import {
  AnswerId,
  AnswerMap,
  CHINTU_BEHAVIOUR,
  IGNORED,
  NO_SHOW_PLACARD_ID,
  beatsAtOrBelow,
  crossedBeat,
  resolveOutcome,
  resolvePlacard,
  thresholdFor,
  validateConfiguration,
} from "./chintuStoryEngine";
import { CHINTU_FINALE } from "./chintuConfiguration";

/**
 * Drives Chintu's arc off the committed bill.
 *
 * Committed = non-rejected items in placed (non-draft) rounds, priced per
 * variant -- the same definition the bill uses. The header chip includes the
 * still-open round, so it can cross a threshold while someone is mid-browsing;
 * celebrating uncommitted picks would both interrupt and overpromise. The
 * trigger therefore lags the visible chip slightly, by design.
 *
 * Answers are per-device. There's no server-side broadcast, so every phone
 * evaluates its own FCM-synced total: placards land within push latency of
 * each other (~0.5-3s), but each diner answers for themselves and gets their
 * own verdict on whether Chintu turns up. One person's "I don't care" can't
 * rob the table.
 *
 * All content, thresholds and the arrival rule live in chintuConfiguration.ts.
 * This file only decides WHEN to consult it.
 *
 * QA: localStorage.setItem("dinein.chintu.force", "<messageId>" | "__noShow__"
 * | "ARRIVE") fires that beat on mount, ignoring thresholds, latches and
 * cooldown.
 */

const FORCE_KEY = "dinein.chintu.force";

/** Placard ids plus one pseudo-entry meaning "go straight to the 3D". */
type QueueItem = string | "ARRIVE_DIRECT";

interface StoredState {
  fired: string[];
  answers: AnswerMap;
  lastFiredAt: number;
}

const EMPTY: StoredState = { fired: [], answers: {}, lastFiredAt: 0 };

const storageKey = (sessionId: string) =>
  `dinein.chintu.${CHINTU_BEHAVIOUR.storageVersion}.${sessionId}`;

const readState = (sessionId: string): StoredState => {
  try {
    const raw = localStorage.getItem(storageKey(sessionId));
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    return {
      fired: Array.isArray(parsed.fired) ? parsed.fired : [],
      answers: parsed.answers ?? {},
      lastFiredAt: parsed.lastFiredAt ?? 0,
    };
  } catch {
    return { ...EMPTY }; // private mode / corrupt entry — degrade to in-memory
  }
};

const writeState = (sessionId: string, state: StoredState) => {
  try {
    localStorage.setItem(storageKey(sessionId), JSON.stringify(state));
  } catch {
    /* in-memory copy still holds for this mount */
  }
};

const motionAllowed = (): boolean =>
  !(
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

const webglAvailable = (): boolean => {
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
};

/** Idempotent: pull the celebration chunk + model into cache ahead of the finale. */
let prewarmed = false;
const prewarm = () => {
  if (prewarmed) return;
  prewarmed = true;
  // Same specifier as DineInSession's React.lazy → same Vite chunk.
  void import("./ChintuCelebration");
  fetch("/models/monkey.glb").catch(() => {
    prewarmed = false; // flaky restaurant wifi — let the finale retry
  });
};

/**
 * Config is string-keyed, so a typo in a rule fails silently rather than
 * loudly. Surfaced once per page load.
 */
let validated = false;
const validateOnce = () => {
  if (validated) return;
  validated = true;
  const problems = validateConfiguration();
  if (problems.length) {
    // eslint-disable-next-line no-console
    console.warn("[chintu] configuration problems:\n  - " + problems.join("\n  - "));
  }
};

interface UseChintuStoryArgs {
  sessionId: string | undefined;
  rounds: DineInRound[];
  roundItems: DineInRoundItem[];
  products: ProductDetailsModal[];
  /** True while a bill/payment sheet is open — beats queue instead of firing. */
  billLocked?: boolean;
}

export interface ChintuStory {
  /** Message id or NO_SHOW_PLACARD_ID; null when nothing is on screen. */
  placard: string | null;
  answers: AnswerMap;
  celebrating: boolean;
  /**
   * Committed total captured the moment the current beat fired — feeds the
   * {amount} token in placard copy and the confetti banner. Snapshotted
   * rather than read live so an order placed while a card is open (or during
   * the 15s dance) can't make the number tick upward mid-moment.
   */
  amount: number;
  shake: boolean;
  answer: (value: AnswerId) => void;
  closePlacard: () => void;
  endCelebration: () => void;
}

export function useChintuStory({
  sessionId,
  rounds,
  roundItems,
  products,
  billLocked = false,
}: UseChintuStoryArgs): ChintuStory {
  const [placard, setPlacard] = useState<string | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [amount, setAmount] = useState(0);

  const stateRef = useRef<StoredState>({ ...EMPTY });
  const prevTotalRef = useRef<number | null>(null);
  const pendingRef = useRef<QueueItem[]>([]);
  const activeRef = useRef(false); // a placard or the finale is on screen
  const answeredRef = useRef(false); // did a button get pressed on this placard?
  const [drainTick, setDrainTick] = useState(0);

  useEffect(validateOnce, []);

  const persist = useCallback(() => {
    if (sessionId) writeState(sessionId, stateRef.current);
  }, [sessionId]);

  // Load persisted arc for this session.
  useEffect(() => {
    if (!sessionId) return;
    stateRef.current = readState(sessionId);
    setAnswers(stateRef.current.answers);
    prevTotalRef.current = null; // re-seed against the current total
  }, [sessionId]);

  // Dev/QA escape hatch.
  useEffect(() => {
    try {
      const forced = localStorage.getItem(FORCE_KEY);
      if (!forced) return;
      // A forced beat has no real crossing, so quote its own milestone
      // instead of the ₹0 amount state starts at.
      if (forced === "ARRIVE") {
        setAmount(CHINTU_FINALE.at);
        activeRef.current = true;
        setCelebrating(true);
      } else if (forced === NO_SHOW_PLACARD_ID || resolvePlacard(forced, {})) {
        setAmount(thresholdFor(forced));
        activeRef.current = true;
        answeredRef.current = false;
        setPlacard(forced);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const enqueue = useCallback((id: QueueItem) => {
    pendingRef.current.push(id);
    setDrainTick((n) => n + 1);
  }, []);

  // ---- crossing detection -------------------------------------------------
  useEffect(() => {
    if (!sessionId) return;

    // Committed = items in placed (non-draft) rounds that the kitchen hasn't
    // rejected -- the same definition the bill uses (DineInSummaryHelper).
    // Rounds now move REQUESTED -> APPROVED -> SERVED, so "placed" replaces
    // the old CLOSED-only check, which would never match.
    const committedItems = billableDineInItems(selectPlacedRounds(rounds, roundItems).items);

    // Prices resolve async (fetchDineInProducts). Evaluating early would
    // undercount and fire at a moment tied to a network response rather than
    // to the order.
    // Priced per variant (Half/Full), exactly like the bill.
    const index = buildProductIndex(products);
    const unitPrices = committedItems.map((item) => resolveDineInLine(item, index).unitPrice);
    if (!unitPrices.every((price) => price != null)) return;

    const total = committedItems.reduce(
      (sum, item, i) => sum + (unitPrices[i] as number) * item.quantity,
      0
    );

    // First fully-resolved evaluation seeds the baseline. Someone joining a
    // table already past a threshold latches it silently — that moment
    // belonged to the people who were here. Note no answer is recorded for
    // those, so only a catch-all rule will match them later.
    if (prevTotalRef.current === null) {
      prevTotalRef.current = total;
      const already = beatsAtOrBelow(total).filter(
        (id) => !stateRef.current.fired.includes(id)
      );
      if (already.length) {
        stateRef.current.fired = [...stateRef.current.fired, ...already];
        persist();
      }
      return;
    }

    const before = prevTotalRef.current;
    prevTotalRef.current = total;

    const beat = crossedBeat(before, total, stateRef.current.fired);
    if (!beat) return;

    // Latch before showing anything: a refresh mid-placard can't replay it.
    stateRef.current.fired = [...stateRef.current.fired, beat.id];
    persist();

    // Freeze the number this beat will quote, before anything can move it.
    setAmount(total);

    if (beat.kind === "finale") {
      const outcome = resolveOutcome(stateRef.current.answers);
      if (outcome === "ARRIVE") {
        prewarm();
        enqueue("ARRIVE_DIRECT");
      } else if (outcome === "NO_SHOW") {
        enqueue(NO_SHOW_PLACARD_ID);
      }
      // "NOTHING" — the arc simply ends.
      return;
    }

    enqueue(beat.id);
  }, [sessionId, rounds, roundItems, products, enqueue, persist]);

  // ---- queue drain --------------------------------------------------------
  useEffect(() => {
    if (activeRef.current || billLocked) return;
    if (!pendingRef.current.length) return;

    const sinceLast = Date.now() - stateRef.current.lastFiredAt;
    const wait = Math.max(0, CHINTU_BEHAVIOUR.cooldownMs - sinceLast);

    const show = () => {
      const next = pendingRef.current.shift();
      if (!next) return;

      if (next === "ARRIVE_DIRECT") {
        if (!webglAvailable()) return;
        activeRef.current = true;
        setCelebrating(true);
        return;
      }

      // Reduced motion doesn't mean no story — the placard still appears, it
      // just doesn't swing or shake (handled in ChintuPlacard's CSS).
      activeRef.current = true;
      answeredRef.current = false;
      stateRef.current.lastFiredAt = Date.now();
      persist();
      setPlacard(next);
    };

    if (wait === 0) {
      show();
      return;
    }
    const timer = setTimeout(() => setDrainTick((n) => n + 1), wait);
    return () => clearTimeout(timer);
  }, [drainTick, billLocked, persist]);

  // ---- outputs ------------------------------------------------------------
  const answer = useCallback(
    (value: AnswerId) => {
      if (!placard || placard === NO_SHOW_PLACARD_ID) return;
      answeredRef.current = true;
      stateRef.current.answers = { ...stateRef.current.answers, [placard]: value };
      persist();
      setAnswers(stateRef.current.answers);

      // If this answer already guarantees he's coming, start fetching the 3D
      // assets now rather than at the crossing.
      if (resolveOutcome(stateRef.current.answers) === "ARRIVE") prewarm();
    },
    [placard, persist]
  );

  const closePlacard = useCallback(() => {
    // Swiped away or tapped outside without pressing anything — record it as
    // IGNORED so config rules can match on silence specifically.
    if (placard && placard !== NO_SHOW_PLACARD_ID && !answeredRef.current) {
      stateRef.current.answers = { ...stateRef.current.answers, [placard]: IGNORED };
      persist();
      setAnswers(stateRef.current.answers);
    }
    setPlacard(null);
    activeRef.current = false;
    setDrainTick((n) => n + 1);
  }, [placard, persist]);

  const endCelebration = useCallback(() => {
    setCelebrating(false);
    activeRef.current = false;
    setDrainTick((n) => n + 1);
  }, []);

  const shake =
    placard !== null &&
    resolvePlacard(placard, answers, amount)?.motion.shake === true &&
    motionAllowed();

  return {
    placard,
    answers,
    celebrating,
    amount,
    shake,
    answer,
    closePlacard,
    endCelebration,
  };
}
